import * as React from 'react';
import * as Styled from './Menu.styled';
import { useHistory, withRouter } from 'react-router-dom';
import IAppState from '../../../App.types';
import { TARReducerName } from '../TAR.resources';
import { ReduxContext } from '@employee-experience/common/lib/ReduxContext';
import { ComponentContext } from '@employee-experience/common/lib/ComponentContext';
import { TelemetryEvents } from '@employee-experience/common/lib/Resources/TelemetryEvents';
import { IInformationState } from '../TimeEntry/Information/Information.types';
import { INITIAL_INFORMATION } from '../TimeEntry/Information/Information.reducer';
import { IBackendJobStatusState } from '../../../Shared/JobStatus/BackendJobStatus.types';
import { MenuItemText, MenuLink } from './Menu.types';
import { AdminRoleType } from '../TimeEntry/Information/information.resources';
import { setAdminActionTypesRedirection } from '../Administration/AdminActionTypes/AdminActionTypes.actions';
import { setPersonnelDetailClearState } from '../TimeEntry/PersonnelDetail/PersonnelDetail.action';
import { setEmployeeFilterSelectedItem } from '../TimeEntry/Filter/EmployeeFilter.actions';
import { setInformationApiUpdateState } from '../TimeEntry/Information/Information.actions';
import { IButtonProps, ICommandBarItemProps, IContextualMenuItem } from '@fluentui/react';

/*eslint no-warning-comments: [2, { "terms": ["todo", "fixme", "any other term"], "location": "anywhere" }]*/
function Menu(): React.ReactElement {
    const { useSelector, dispatch } = React.useContext(ReduxContext);
    const { telemetryClient } = React.useContext(ComponentContext);
    const dirtyFlag: boolean = useSelector((state: IAppState) => {
        const tar = state.dynamic[TARReducerName];
        const timeEntryList = tar && tar.timeEntry && tar.timeEntry.timeEntryList;
        return timeEntryList
            ? timeEntryList.find(x => x.weekDayDetails.find(d => d.dirty === true)) !== undefined
            : false;
    });
    const info: IInformationState = useSelector((state: IAppState) => {
        const tar = state.dynamic[TARReducerName];
        return tar ? tar.information : INITIAL_INFORMATION;
    });

    const jobStatus: IBackendJobStatusState = useSelector((state: IAppState) => {
        const tar = state.dynamic[TARReducerName];
        return tar ? tar.backendJobStatus : { isJobRunning: false };
    });
    const history = useHistory();
    React.useEffect(() => {
        if (info) document.title = `${__APP_NAME__} - ${MenuItemText.get(history.location.pathname)}`;
    }, [info]);
    const onMenuClick = React.useCallback(
        (
            e?: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>,
            item?: IContextualMenuItem | undefined
        ): boolean | void | undefined => {
            if (item) {
                if (history.location.pathname === `/${item.key}`) return;
                if (dirtyFlag === true && item.title !== MenuLink.TIME_ENTRY_LINK) {
                    const resolved = confirm('You have unsaved changes, do you want to continue?');
                    if (resolved === true) {
                        history.push(`/${item.key}`);
                        telemetryClient.trackTrace({
                            message: TelemetryEvents.LinkClicked,
                            properties: { to: `/${item.key}`, title: item.title }
                        });
                    } else {
                        return;
                    }
                } else {
                    history.push(`/${item.key}`);

                    telemetryClient.trackTrace({
                        message: TelemetryEvents.LinkClicked,
                        properties: { to: `/${item.key}`, title: item.title }
                    });
                }
                dispatch(setAdminActionTypesRedirection({ redirection: undefined }));
                dispatch(setInformationApiUpdateState(false));
                if (item.title !== MenuLink.TIME_ENTRY_LINK) {
                    dispatch(setPersonnelDetailClearState(true));
                    dispatch(setEmployeeFilterSelectedItem({ selectedItems: [] }));
                }
            }
        },
        [dirtyFlag, dispatch, history, telemetryClient]
    );

    const overflowButtonProps: IButtonProps = {
        ariaLabel: 'More commands',
        menuIconProps: { iconName: 'More' }
    };

    const menuItems = (): ICommandBarItemProps[] => {
        const items: ICommandBarItemProps[] = [];
        if (info.tarUser || info.isAdmin) {
            items.push({
                key: 'tar',
                text: 'Time Entry',
                ariaLabel: 'Navigation landmark list tab item',
                onClick: onMenuClick,
                title: 'Time Entry',
                className: history.location.pathname === '/tar' ? 'menuItemSelected' : ''
            });
        }
        if (info.tarUser || info.isAdmin || info.isFormerTARUser || info.isTARManager) {
            items.push({
                key: 'reports',
                text: 'Reporting',
                ariaLabel: 'Navigation landmark list tab item',
                onClick: onMenuClick,
                title: 'Reporting',
                className: history.location.pathname === '/reports' ? 'menuItemSelected' : ''
            });
        }
        if (info.isAdmin === true && info.adminRoleTypeCode !== AdminRoleType.TARHRAdmin) {
            items.push({
                key: 'administration',
                text: 'Administration',
                ariaLabel: 'Navigation landmark list tab item',
                onClick: onMenuClick,
                title: 'Administration',
                className: history.location.pathname === '/administration' ? 'menuItemSelected' : ''
            });
        }
        if (
            info.isAdmin === true &&
            info.adminRoleTypeCode !== AdminRoleType.TARHRAdmin &&
            info.adminRoleTypeCode !== AdminRoleType.TARRetailAdmin
        ) {
            items.push({
                key: 'bulktimeentry',
                text: 'Bulk Time Entry',
                ariaLabel: 'Navigation landmark list tab item',
                onClick: onMenuClick,
                title: 'Bulk Time Entry',
                className: history.location.pathname === '/bulktimeentry' ? 'menuItemSelected' : ''
            });
        }
        if (info.isAdmin === true && info.adminRoleTypeCode === AdminRoleType.TARAdmin) {
            items.push({
                key: 'splashpage',
                text: 'Splash Administration',
                ariaLabel: 'Navigation landmark list tab item',
                onClick: onMenuClick,
                title: 'Splash Administration',
                className: history.location.pathname === '/splashpage' ? 'menuItemSelected' : ''
            });
        }
        return items;
    };

    return (
        <Styled.MenuStack role="navigation" horizontal verticalAlign="center">
            {jobStatus.isJobRunning === false && info.untrackedUser === false && (
                <Styled.MenuCommandBar overflowButtonProps={overflowButtonProps} items={menuItems()} />
            )}
        </Styled.MenuStack>
    );
}

const connected = withRouter(Menu);
export { connected as Menu };
